(function () {
  'use strict';

  if (window.customElements && customElements.get('feature-accordion')) return;

  var EASING = 'cubic-bezier(0.25, 0.8, 0.3, 1)';
  var MIN_MS = 240;
  var MAX_MS = 480;
  var PX_PER_MS = 1.6;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function canAnimate(el) {
    return typeof el.animate === 'function' && !prefersReducedMotion();
  }

  function durationFor(distance, floor) {
    var min = typeof floor === 'number' ? floor : MIN_MS;
    return Math.min(MAX_MS, Math.max(min, Math.round(Math.abs(distance) / PX_PER_MS)));
  }

  var CHECK_SVG =
    '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
    '<circle cx="8" cy="8" r="8" fill="currentColor"/>' +
    '<path d="M4.6 8.3 6.9 10.6 11.4 5.9" stroke="#FFFFFF" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  class FeatureAccordionEl extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('[data-fa-details]');
      this.summary = this.querySelector('[data-fa-summary]');
      this.content = this.querySelector('[data-fa-content]');
      this.body = this.querySelector('[data-fa-body]') || (this.content && this.content.firstElementChild);

      if (!this.details || !this.summary || !this.content || !this.body) return;

      this.animation = null;
      this.bodyAnimation = null;
      this.suppressToggle = false;

      this._onSummaryClick = this.onSummaryClick.bind(this);
      this.summary.addEventListener('click', this._onSummaryClick);

      this._onToggle = () => {
        if (this.suppressToggle || !this.details.open) return;
        if (!this.animation) this.stopAnimations();
        this.closeSiblings();
      };
      this.details.addEventListener('toggle', this._onToggle);

      this.observeContentSize();

      if (this.dataset.variantBound === 'true') this.initVariantSync();
    }

    disconnectedCallback() {
      if (this.summary && this._onSummaryClick) {
        this.summary.removeEventListener('click', this._onSummaryClick);
      }
      if (this.details && this._onToggle) {
        this.details.removeEventListener('toggle', this._onToggle);
      }
      if (this._onVariantChange) {
        document.removeEventListener('variant:change', this._onVariantChange);
        document.removeEventListener('variantChange', this._onVariantChange);
      }
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.stopAnimations();
    }

    currentHeight() {
      return this.content.getBoundingClientRect().height;
    }

    targetHeight() {
      return this.body.getBoundingClientRect().height;
    }

    stopAnimations() {
      this.animation = null;
      this.bodyAnimation = null;

      [this.content, this.body].forEach(function (el) {
        if (el && typeof el.getAnimations === 'function') {
          el.getAnimations().forEach(function (animation) {
            animation.cancel();
          });
        }
      });
    }

    setAnimating(state) {
      this.classList.toggle('is-animating', state);
    }

    onSummaryClick(event) {
      if (!canAnimate(this.content)) return;

      event.preventDefault();

      if (this.details.open) {
        this.collapse();
        return;
      }

      this.closeSiblings();
      this.expand();
    }

    expand() {
      if (!canAnimate(this.content)) {
        this.details.open = true;
        return;
      }

      var from = this.details.open ? this.currentHeight() : 0;

      this.stopAnimations();
      this.suppressToggle = true;
      this.details.open = true;
      this.suppressToggle = false;

      var to = this.targetHeight();
      if (to <= 0) return;

      var duration = durationFor(to - from);
      this.setAnimating(true);
      this.content.style.height = from + 'px';

      this.animation = this.content.animate(
        { height: [from + 'px', to + 'px'] },
        { duration: duration, easing: EASING }
      );

      this.bodyAnimation = this.body.animate(
        { opacity: [from > 0 ? 1 : 0, 1], transform: ['translateY(-4px)', 'translateY(0)'] },
        { duration: Math.round(duration * 0.8), easing: 'ease-out', fill: 'backwards' }
      );

      this.onAnimationSettled(() => {
        this.content.style.height = '';
        this.setAnimating(false);
        this.rememberHeight();
      });
    }

    collapse() {
      if (!canAnimate(this.content)) {
        this.details.open = false;
        return;
      }
      if (!this.details.open) return;

      var from = this.currentHeight();
      this.stopAnimations();

      var duration = durationFor(from);
      this.setAnimating(true);
      this.content.style.height = from + 'px';

      this.animation = this.content.animate(
        { height: [from + 'px', '0px'] },
        { duration: duration, easing: EASING }
      );

      this.bodyAnimation = this.body.animate(
        { opacity: [1, 0] },
        { duration: Math.round(duration * 0.6), easing: 'ease-in', fill: 'forwards' }
      );

      this.onAnimationSettled(() => {
        this.suppressToggle = true;
        this.details.open = false;
        this.suppressToggle = false;
        this.content.style.height = '';
        this.setAnimating(false);
      });
    }

    onAnimationSettled(callback) {
      var animation = this.animation;
      animation.finished
        .then(() => {
          if (this.animation !== animation) return;
          this.animation = null;
          this.bodyAnimation = null;
          callback();
        })
        .catch(function () {
        });
    }

    closeSiblings() {
      var group = this.dataset.group;
      if (!group) return;

      var selector = 'feature-accordion[data-group="' + group.replace(/"/g, '\\"') + '"]';

      document.querySelectorAll(selector).forEach((el) => {
        if (el === this || typeof el.collapse !== 'function') return;
        if (el.details && el.details.open) el.collapse();
      });
    }

    rememberHeight() {
      this._lastHeight = this.details.open ? this.targetHeight() : 0;
    }

    observeContentSize() {
      if (typeof ResizeObserver !== 'function') return;

      this.rememberHeight();

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.details.open || this.animation) return;

        var next = this.targetHeight();
        var previous = this._lastHeight;
        this._lastHeight = next;

        if (!canAnimate(this.content)) return;
        if (previous == null || Math.abs(next - previous) < 2) return;

        this.setAnimating(true);
        this.animation = this.content.animate(
          { height: [previous + 'px', next + 'px'] },
          { duration: durationFor(next - previous, 260), easing: EASING }
        );

        this.onAnimationSettled(() => {
          this.content.style.height = '';
          this.setAnimating(false);
        });
      });

      this.resizeObserver.observe(this.body);
    }

    initVariantSync() {
      var script = this.querySelector('[data-fa-variants]');
      if (!script) return;

      try {
        this.variantData = JSON.parse(script.textContent);
      } catch (error) {
        return;
      }
      if (!this.variantData) return;

      this.maxItems = parseInt(this.dataset.maxItems, 10) || 10;
      this.marker = this.dataset.marker || 'check';

      this._onVariantChange = (event) => {
        var variant = event && event.detail && event.detail.variant;
        if (!variant) return;
        this.renderVariant(this.variantData[variant.id]);
      };

      document.addEventListener('variant:change', this._onVariantChange);
      document.addEventListener('variantChange', this._onVariantChange);
    }

    renderVariant(entry) {
      if (!entry) return;

      var titleEl = this.querySelector('[data-fa-title]');
      if (titleEl && entry.title) titleEl.textContent = entry.title;

      var list = this.querySelector('[data-fa-list]');
      if (!list || !Array.isArray(entry.items)) return;

      var items = entry.items
        .filter(function (text) {
          return typeof text === 'string' && text.trim() !== '';
        })
        .slice(0, this.maxItems);

      list.replaceChildren.apply(
        list,
        items.map((text, index) => this.buildItem(text, index))
      );
    }

    buildItem(text, index) {
      var li = document.createElement('li');
      li.className = 'feature-accordion__item';

      if (this.marker !== 'none') {
        var marker = document.createElement('span');
        marker.setAttribute('aria-hidden', 'true');

        if (this.marker === 'number') {
          marker.className = 'feature-accordion__marker feature-accordion__marker--number';
          marker.textContent = String(index + 1);
        } else {
          marker.className = 'feature-accordion__marker';
          marker.innerHTML = CHECK_SVG;
        }
        li.appendChild(marker);
      }

      var span = document.createElement('span');
      span.className = 'feature-accordion__text';
      span.textContent = text;
      li.appendChild(span);

      return li;
    }
  }

  customElements.define('feature-accordion', FeatureAccordionEl);
})();
